import { useState } from "react";
import { toast } from "sonner";
import { Upload, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { GlassCard, GradButton, fmtMoney } from "@/components/erp";
import api, { errMsg } from "@/lib/api";

const tamam = (r) => r.on && Object.values(r.on.kontrol).length > 0 && Object.values(r.on.kontrol).every((k) => k.tutuyor);
const bakiyeTutar = (r) => r.sonuc && Object.entries(r.on.kontrol).every(([pb, k]) => Math.abs((r.sonuc.risk?.bakiyeler?.[pb] || 0) - k.pdf.bakiye) < 0.05);

const Kontrol = ({ r }) => (
  <div className="text-xs space-y-0.5">{Object.entries(r.on.kontrol).map(([pb, k]) => (
    <div key={pb} className={k.tutuyor ? "text-emerald-300" : "text-red-300"}>{pb}: {fmtMoney(k.pdf.bakiye, pb)}{!k.tutuyor && ` (okunan ${fmtMoney(k.okunan.borc - k.okunan.alacak, pb)})`}</div>))}</div>
);

const Durum = ({ r, i }) => {
  if (r.hata) return <span className="text-xs text-red-300" data-testid={`toplu-ekstre-hata-${i}`}>{r.hata}</span>;
  if (r.calisiyor) return <Loader2 className="w-4 h-4 animate-spin" />;
  if (r.sonuc) return <span className={`text-xs inline-flex items-center gap-1 ${bakiyeTutar(r) ? "text-emerald-300" : "text-amber-300"}`} data-testid={`toplu-ekstre-sonuc-${i}`}>
    <CheckCircle2 className="w-4 h-4" />{r.sonuc.eklenen} satır aktarıldı · {Object.entries(r.sonuc.risk?.bakiyeler || {}).map(([pb, v]) => fmtMoney(v, pb)).join(" · ")}{bakiyeTutar(r) ? " · PDF ile aynı" : " · PDF ile farklı"}</span>;
  return tamam(r) ? <span className="text-xs text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Toplamlar tutuyor</span>
    : <span className="text-xs text-red-300 inline-flex items-center gap-1"><XCircle className="w-4 h-4" />Toplamlar tutmuyor, aktarılmaz</span>;
};

const Satir = ({ r, i }) => (
  <tr className="border-t border-white/5" data-testid={`toplu-ekstre-satir-${i}`}>
    <td className="p-2 text-xs max-w-[160px] truncate" title={r.ad}>{r.ad}</td>
    <td className="p-2 text-xs">{r.on ? <><div className="font-medium">{r.on.unvan}</div><div className="text-muted-foreground">{r.on.kod} · {r.on.cari ? "sistemdeki cariye" : "yeni cari açılacak"}</div></> : "—"}</td>
    <td className="p-2 text-xs text-right">{r.on ? <>{r.on.satir}<div className="text-muted-foreground">{r.on.mevcut_aktarim ? `${r.on.mevcut_aktarim} eski satır silinir` : ""}</div></> : ""}</td>
    <td className="p-2">{r.on && <Kontrol r={r} />}</td>
    <td className="p-2"><Durum r={r} i={i} /></td>
  </tr>
);

export const TopluEkstre = () => {
  const [liste, setListe] = useState([]);
  const [busy, setBusy] = useState(false);
  const guncelle = (i, p) => setListe((l) => l.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const gonder = async (url, file) => { const fd = new FormData(); fd.append("dosya", file); return (await api.post(url, fd)).data; };
  const sec = async (files) => {
    const yeni = [...files].map((f) => ({ ad: f.name, file: f, calisiyor: true }));
    setListe(yeni); setBusy(true);
    for (let i = 0; i < yeni.length; i++) {
      try { guncelle(i, { on: await gonder("/kolaybi-ekstre/onizle", yeni[i].file), calisiyor: false }); } catch (e) { guncelle(i, { hata: errMsg(e), calisiyor: false }); }
    }
    setBusy(false);
  };
  const aktar = async () => {
    setBusy(true);
    let n = 0;
    for (let i = 0; i < liste.length; i++) {
      if (!tamam(liste[i]) || liste[i].sonuc) continue;
      guncelle(i, { calisiyor: true });
      try { guncelle(i, { sonuc: await gonder("/kolaybi-ekstre/aktar", liste[i].file), calisiyor: false }); n++; } catch (e) { guncelle(i, { hata: errMsg(e), calisiyor: false }); }
    }
    setBusy(false); toast.success(`${n} firmanın ekstresi aktarıldı`);
  };
  const hazir = liste.filter((r) => tamam(r) && !r.sonuc).length;
  return (
    <GlassCard className="p-6 space-y-4" data-testid="toplu-ekstre">
      <div><h3 className="font-head font-semibold">KolayBi · Cari Hesap Ekstreleri (toplu)</h3>
        <p className="text-xs text-muted-foreground mt-1">Birden çok firmanın KolayBi ekstre PDF'ini birlikte seçin. Her dosya okunur ve PDF'teki toplamlarla karşılaştırılır; yalnız tutanlar aktarılır. Daha önce aktarılmış KolayBi satırları silinip yenisiyle değiştirilir, sistemde olmayan cari ekstredeki bilgilerle açılır.</p></div>
      <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm grad-bg text-white cursor-pointer" data-testid="toplu-ekstre-label">
        <Upload className="w-4 h-4" />PDF dosyalarını seç
        <input type="file" accept=".pdf" multiple className="hidden" data-testid="toplu-ekstre-dosya" disabled={busy} onChange={(e) => { sec(e.target.files || []); e.target.value = ""; }} />
      </label>
      {liste.length > 0 && <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="toplu-ekstre-tablo">
        <thead><tr className="text-xs text-muted-foreground text-left"><th className="p-2">Dosya</th><th className="p-2">Firma</th><th className="p-2 text-right">Satır</th><th className="p-2">PDF bakiyesi</th><th className="p-2">Durum</th></tr></thead>
        <tbody>{liste.map((r, i) => <Satir key={`${r.ad}-${i}`} r={r} i={i} />)}</tbody></table></div>}
      {liste.length > 0 && <div className="flex justify-end"><GradButton data-testid="toplu-ekstre-aktar" disabled={busy || !hazir} onClick={aktar}>{busy ? "İşleniyor…" : `${hazir} firmanın ekstresini aktar`}</GradButton></div>}
    </GlassCard>
  );
};
